/**
 * V70 타자왕 — 게임 엔진 (pure logic)
 *
 * 설계 원칙 (사용자 지시):
 *   - 일시정지 기능 없음. 포커스/탭 변경에도 시간 계속 흐름.
 *   - 서버 시간 기준 (startedAtServerMs) + 로컬 performance.now() 모니터.
 *   - 결정론적 단어 큐 (Mulberry32 + seed) → Phase 3 서버와 동기화 가능.
 *   - 자동 입력 방지 과장 금지. 사전은 공개됨. commitment 는 재계산용.
 *
 * React 비의존. useAcidRainEngine hook 에서 wrapping.
 */
import {
  WORD_POOLS,
  DIFFICULTY_FALLBACK_ORDER,
  WORD_DICTIONARY_VERSION,
  getBaseScore,
} from './wordDictionary';
import type { Difficulty } from './wordDictionary';
import { createPrng, hashSeed } from './prng';
import type {
  AcidRainSettings,
  FallingWord,
  GameEvent,
  GameResult,
  GameSnapshot,
  GameStatus,
} from './types';

/**
 * 단어 생성 알고리즘 버전 (Phase 3 서버 재계산 호환성).
 *
 *   v1 — 난이도 추첨 후 pool 전체에서 균등 선택 (화면 중복 허용)
 *   v2 — 화면에 떠 있는 단어를 후보에서 제외 (중복 0). PRNG 소비 횟수는 v1 과
 *        동일하게 spawn 당 3회(난이도·단어·x좌표)로 고정했으나, **후보 집합이
 *        달라지므로 같은 seed 라도 v1 과 다른 단어 큐가 나온다.**
 *
 * seed 만으로는 재현을 보장할 수 없다 —
 * `(seed, SPAWN_ALGORITHM_VERSION, WORD_DICTIONARY_VERSION)` 3개가 모두 같아야 한다.
 */
export const SPAWN_ALGORITHM_VERSION = 2;

const DEFAULT_FIELD_HEIGHT_PX = 480;
const INITIAL_FALL_SPEED_PX_SEC = 60;
const INITIAL_SPAWN_INTERVAL_MS = 1500;
const COMBO_BONUS_PCT_PER_STEP = 0.05; // 5% per combo (max 100%)
const COMBO_BONUS_MAX_PCT = 1.0;

interface EngineConfig {
  settings: AcidRainSettings;
  sessionId: string;
  seed: string;
  startedAtServerMs: number;
  fieldHeightPx?: number;
}

export class AcidRainEngine {
  private config: EngineConfig;
  private rand: () => number;
  private status: GameStatus = 'idle';

  private startedPerfMs: number = 0;
  private lastTickPerfMs: number = 0;

  private score = 0;
  private combo = 0;
  private maxCombo = 0;
  private lives: number;
  private cleared = 0;
  private missed = 0;

  private wordIdCounter = 0;
  private fallingWords: FallingWord[] = [];
  private typingBuffer = '';
  private composing = false;

  private nextSpawnMs: number = 0;
  private difficultyLevel = 0;
  private curFallSpeed = INITIAL_FALL_SPEED_PX_SEC;
  private curSpawnInterval = INITIAL_SPAWN_INTERVAL_MS;

  private events: GameEvent[] = [];

  constructor(config: EngineConfig) {
    this.config = config;
    this.rand = createPrng(hashSeed(config.seed));
    this.lives = config.settings.initialLives;
  }

  /** 게임 시작 (countdown 종료 후 호출) */
  start(perfNow: number): void {
    this.status = 'playing';
    this.startedPerfMs = perfNow;
    this.lastTickPerfMs = perfNow;
    this.nextSpawnMs = this.curSpawnInterval;
    this.pushEvent('start', 0);
  }

  /** 매 프레임 호출 (requestAnimationFrame) */
  tick(perfNow: number): GameSnapshot {
    if (this.status !== 'playing') return this.snapshot();

    const elapsedMs = perfNow - this.startedPerfMs;
    const deltaSec = (perfNow - this.lastTickPerfMs) / 1000;
    this.lastTickPerfMs = perfNow;

    // 난이도 증가 (매 difficultyRampSec 초마다)
    const rampStep = Math.floor(elapsedMs / 1000 / this.config.settings.difficultyRampSec);
    if (rampStep > this.difficultyLevel) {
      const steps = rampStep - this.difficultyLevel;
      for (let i = 0; i < steps; i++) {
        this.curFallSpeed *= this.config.settings.fallSpeedMultiplier;
        this.curSpawnInterval /= this.config.settings.spawnIntervalDivisor;
      }
      this.difficultyLevel = rampStep;
      this.pushEvent('difficulty_ramp', elapsedMs, { level: rampStep });
    }

    // 단어 생성
    while (elapsedMs >= this.nextSpawnMs) {
      this.spawnWord(this.nextSpawnMs);
      this.nextSpawnMs += this.curSpawnInterval;
    }

    // 단어 낙하 + 바닥 충돌 처리
    const fieldHeight = this.config.fieldHeightPx ?? DEFAULT_FIELD_HEIGHT_PX;
    const survived: FallingWord[] = [];
    for (const w of this.fallingWords) {
      const ageSec = (elapsedMs - w.spawnMs) / 1000;
      const yPx = ageSec * w.speed;
      if (yPx >= fieldHeight) {
        this.handleMiss(w, elapsedMs);
      } else {
        survived.push(w);
      }
    }
    this.fallingWords = survived;

    // 시간 종료
    const remainingSec = Math.max(0, this.config.settings.durationSec - elapsedMs / 1000);
    if (remainingSec <= 0 && this.status === 'playing') {
      this.end('time_up', elapsedMs);
    }

    void deltaSec;
    return this.snapshot();
  }

  /**
   * 단어 생성 (알고리즘 v2).
   *
   * 중복 방지: **화면에 떠 있는 단어와 같은 문자열은 생성하지 않는다.**
   *   - 제거되거나 바닥에 도달해 사라진 뒤에는 다시 등장할 수 있다.
   *   - 선택된 난이도 pool 에 쓸 수 있는 단어가 없으면
   *     `DIFFICULTY_FALLBACK_ORDER` 의 고정 순서로 다른 pool 을 탐색한다.
   *   - 세 pool 이 모두 소진된 이론적 경우에만 중복을 허용한다 (**항상 종료**).
   *
   * 결정론 유지: PRNG 는 spawn 당 정확히 3회만 소비한다
   *   (1) 난이도 추첨  (2) 단어 선택  (3) x 좌표
   *   후보가 비어도 **추가 추첨을 하지 않으므로** PRNG 스트림이 어긋나지 않는다.
   *   재시도 루프가 없어 무한 루프도 발생할 수 없다.
   */
  private spawnWord(spawnMs: number): void {
    const diffRoll = this.rand();
    const t = Math.min(1, spawnMs / (this.config.settings.durationSec * 1000));
    const hardChance = 0.05 + t * 0.25;
    const mediumChance = 0.3 + t * 0.3;
    const difficulty: Difficulty =
      diffRoll < hardChance ? 'hard'
        : diffRoll < hardChance + mediumChance ? 'medium'
          : 'easy';

    const pickRoll = this.rand();
    const active = new Set(this.fallingWords.map((fw) => fw.text));
    let candidates: readonly string[] | null = null;
    for (const d of DIFFICULTY_FALLBACK_ORDER[difficulty]) {
      const free = WORD_POOLS[d].filter((word) => !active.has(word));
      if (free.length > 0) {
        candidates = free;
        break;
      }
    }
    // 전 pool 소진 (현실적으로 불가 — 최소 pool 34개 vs 동시 표시 ~7개).
    // 생성을 건너뛰면 난이도 곡선이 깨지므로 중복을 허용해 반드시 1개를 만든다.
    const list = candidates ?? WORD_POOLS[difficulty];
    const text = list[Math.floor(pickRoll * list.length)];

    const xRatio = 0.05 + this.rand() * 0.9;
    const w: FallingWord = {
      id: this.wordIdCounter++,
      text,
      xRatio,
      spawnMs,
      speed: this.curFallSpeed,
      // 점수는 **단어가 실제로 속한 난이도**로 결정한다 (사전 역인덱스).
      // fallback 으로 다른 pool 에서 뽑혔을 때도 점수가 어긋나지 않는다.
      baseScore: getBaseScore(text),
    };
    this.fallingWords.push(w);
    this.pushEvent('word_spawn', spawnMs, { id: w.id, text: w.text });
  }

  private handleMiss(w: FallingWord, elapsedMs: number): void {
    this.missed += 1;
    const brokenCombo = this.combo;
    this.combo = 0;
    this.lives -= 1;
    this.pushEvent('word_missed', elapsedMs, { id: w.id, text: w.text });
    if (brokenCombo > 0) this.pushEvent('combo_broken', elapsedMs, { lostCombo: brokenCombo });
    this.pushEvent('life_lost', elapsedMs, { remaining: this.lives });
    if (this.lives <= 0) this.end('no_lives', elapsedMs);
  }

  // ──────── 입력 처리 (한글 IME 지원) ────────

  onCompositionStart(): void {
    this.composing = true;
  }

  onCompositionEnd(composedText: string): void {
    this.composing = false;
    this.typingBuffer = composedText;
  }

  onInput(value: string): void {
    this.typingBuffer = value;
    // 자동 매칭 없음. Enter 전용 (사용자 지시 §3)
  }

  /**
   * Enter 로 제출 (정책):
   *   - 조합 중 / 빈 입력: noop → { matched: false, hadInput: false }
   *   - 정답: 단어 제거, 점수/콤보 증가 → { matched: true }
   *   - 오답: 아무 변동 없음 (콤보/생명력 유지) → { matched: false, hadInput: true }
   */
  submit(): { matched: boolean; hadInput: boolean } {
    if (this.composing) return { matched: false, hadInput: false };
    if (this.status !== 'playing') return { matched: false, hadInput: false };
    const buf = this.typingBuffer.trim();
    if (!buf) return { matched: false, hadInput: false };
    const idx = this.fallingWords.findIndex((w) => w.text === buf);
    if (idx < 0) return { matched: false, hadInput: true };
    const w = this.fallingWords[idx];
    const elapsedMs = this.lastTickPerfMs - this.startedPerfMs;

    this.combo += 1;
    if (this.combo > this.maxCombo) this.maxCombo = this.combo;
    const comboBonusPct = Math.min(COMBO_BONUS_MAX_PCT, (this.combo - 1) * COMBO_BONUS_PCT_PER_STEP);
    const gained = Math.round(w.baseScore * (1 + comboBonusPct));
    this.score += gained;
    this.cleared += 1;

    this.fallingWords.splice(idx, 1);
    this.typingBuffer = '';
    this.pushEvent('word_cleared', elapsedMs, {
      id: w.id,
      text: w.text,
      gained,
      combo: this.combo,
    });
    return { matched: true, hadInput: true };
  }

  /** 입력창 수동 비우기 (오답 제출 후 UI 가 호출) */
  clearBuffer(): void {
    this.typingBuffer = '';
  }

  // ──────── 종료 ────────

  end(reason: GameResult['reason'], elapsedMs?: number): GameResult {
    if (this.status === 'ended') return this.buildResult(reason, elapsedMs ?? 0);
    this.status = 'ended';
    const eMs = elapsedMs ?? this.lastTickPerfMs - this.startedPerfMs;
    this.pushEvent('end', eMs, { reason });
    return this.buildResult(reason, eMs);
  }

  private buildResult(reason: GameResult['reason'], elapsedMs: number): GameResult {
    return {
      sessionId: this.config.sessionId,
      startedAt: this.config.startedAtServerMs,
      endedAt: this.config.startedAtServerMs + elapsedMs,
      elapsedMs,
      clientReportedScore: this.score,
      cleared: this.cleared,
      missed: this.missed,
      maxCombo: this.maxCombo,
      reason,
      // Phase 3 서버가 점수를 재계산할 때 같은 단어 큐를 만들 수 있는지 판정하는 키
      dictionaryVersion: WORD_DICTIONARY_VERSION,
      spawnAlgorithmVersion: SPAWN_ALGORITHM_VERSION,
    };
  }

  private pushEvent(type: GameEvent['type'], tMs: number, payload?: Record<string, unknown>): void {
    this.events.push({ type, tMs, payload });
  }

  getEvents(): readonly GameEvent[] {
    return this.events;
  }

  snapshot(): GameSnapshot {
    const elapsedMs =
      this.status === 'playing' || this.status === 'ended'
        ? Math.max(0, this.lastTickPerfMs - this.startedPerfMs)
        : 0;
    const remainingSec = Math.max(0, this.config.settings.durationSec - elapsedMs / 1000);
    return {
      status: this.status,
      score: this.score,
      combo: this.combo,
      maxCombo: this.maxCombo,
      lives: this.lives,
      remainingSec,
      elapsedMs,
      cleared: this.cleared,
      missed: this.missed,
      fallingWords: this.fallingWords,
      typingBuffer: this.typingBuffer,
      nextSpawnMs: this.nextSpawnMs,
      difficultyLevel: this.difficultyLevel,
    };
  }
}
